import { Link } from 'react-router-dom';
import { api } from '~/api/client';
import { ApplicationRow } from '~/features/marketplace/ApplicationCard';
import { Button, ErrorPanel, Section, SkeletonGrid, formatCount } from '~/shared/ui';
import { useQuery } from '~/shared/hooks/useQuery';
import { useSession } from '~/features/account/SessionProvider';

/**
 * The landing page (§46).
 *
 * All three curated rows come from a single request, because the API resolves and caches them as one
 * document — three separate calls would be three cache lookups for a page that always shows all
 * three.
 */
export function HomePage(): JSX.Element {
  const feed = useQuery((signal) => api.discover(signal), []);
  const categories = useQuery((signal) => api.categories(signal), []);

  return (
    <>
      <Hero />
      <HowItWorks />

      {feed.error !== undefined ? (
        <ErrorPanel error={feed.error} onRetry={feed.reload} />
      ) : feed.data === undefined ? (
        <SkeletonGrid />
      ) : (
        <>
          {feed.data.featured.length > 0 ? (
            <Section title="Featured" description="Hand-picked applications worth a look.">
              <ApplicationRow applications={feed.data.featured} />
            </Section>
          ) : null}

          <Section
            title="Most installed"
            description="What people are actually running."
            action={
              <Link
                to="/applications?sort=popular"
                className="text-sm font-medium text-brand-soft hover:underline"
              >
                See all
              </Link>
            }
          >
            <ApplicationRow applications={feed.data.popular} />
          </Section>

          <Section
            title="Recently updated"
            description="New releases from the last few days."
            action={
              <Link
                to="/applications?sort=newest"
                className="text-sm font-medium text-brand-soft hover:underline"
              >
                See all
              </Link>
            }
          >
            <ApplicationRow applications={feed.data.recentlyUpdated} />
          </Section>
        </>
      )}

      {categories.data === undefined ? null : (
        <Section
          title="Browse by category"
          description="Start from the kind of work you want the application to do."
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {categories.data.items.map((category) => (
              <Link
                key={category.slug}
                to={`/applications?category=${encodeURIComponent(category.slug)}`}
                className="card-hover group"
              >
                <p className="font-semibold text-slate-100 group-hover:text-white">{category.name}</p>
                <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-slate-400">
                  {category.description}
                </p>
                <p className="mt-4 text-xs font-medium text-slate-500">
                  {formatCount(category.applicationCount)} applications →
                </p>
              </Link>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

function Hero(): JSX.Element {
  const { isDeveloper, isSignedIn } = useSession();

  return (
    <section className="relative mb-12 overflow-hidden rounded-[1.75rem] border border-slate-800/90 bg-hero-radial shadow-panel">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-grid-fade bg-grid opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
      />
      <div className="relative px-6 py-10 sm:px-10 sm:py-14 lg:px-12">
        <p className="eyebrow">WorkerHub</p>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl sm:leading-[1.08]">
          Install edge applications into your Cloudflare account — with every permission visible.
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
          Browse analyzed Workers and tools, connect with Cloudflare, and deploy in guided steps. No
          pasted API tokens. No mystery scopes.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/applications">
            <Button variant="primary" className="px-5">
              Browse applications
            </Button>
          </Link>
          <Link to={isSignedIn ? (isDeveloper ? '/developer' : '/developer/register') : '/signin'}>
            <Button variant="secondary" className="px-5">
              {isSignedIn && isDeveloper
                ? 'Open publishing console'
                : isSignedIn
                  ? 'Become a publisher'
                  : 'Sign in with Cloudflare'}
            </Button>
          </Link>
          <Link to="/publish" className="inline-flex items-center text-sm font-medium text-brand-soft hover:underline">
            How to publish →
          </Link>
        </div>
        <dl className="mt-10 grid gap-4 sm:grid-cols-3">
          <Stat label="Before you install" value="Permissions & resources shown" />
          <Stat label="How you sign in" value="Cloudflare OAuth only" />
          <Stat label="What gets scanned" value="Source, config, outbound calls" />
        </dl>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-950/35 px-4 py-3 backdrop-blur-sm">
      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-200">{value}</dd>
    </div>
  );
}

function HowItWorks(): JSX.Element {
  const steps = [
    {
      n: '01',
      title: 'Find an application',
      detail: 'Filter by category, risk, and pricing. Open a listing to read the security report.',
    },
    {
      n: '02',
      title: 'Sign in with Cloudflare',
      detail: 'Authorize once. We store a scoped credential — never an API token you paste into a form.',
    },
    {
      n: '03',
      title: 'Install in guided steps',
      detail: 'Resources are provisioned, the Worker is deployed, and you can retry or undo if anything fails.',
    },
  ] as const;

  return (
    <section className="mb-14">
      <div className="mb-5">
        <p className="eyebrow">How it works</p>
        <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-slate-50">
          From browse to running in three clear moves
        </h2>
      </div>
      <ol className="grid gap-3 lg:grid-cols-3">
        {steps.map((step) => (
          <li key={step.n} className="card relative overflow-hidden">
            <span className="font-display text-3xl font-extrabold text-brand/25">{step.n}</span>
            <h3 className="mt-2 font-semibold text-slate-100">{step.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.detail}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
